import * as React from 'react';
// import Select from "react-select"

// Context
import { ProfileFormContext } from '@/domains/profiles';
import JobTypes from './form-components/JobTypes';
import { ACTIONS } from './ProfileFormReducer';
import ValidatedFormField from './ValidatedFormField';
import { validateHourlyRate, validateTravelDistance } from '../../utils/validation';
import ProTip from './ProTip';

export default function Availability() {
  const [state, dispatch] = React.useContext(ProfileFormContext);

  // Set Field
  const setField = (e) => {
    // Clear any existing error for this field when user starts typing
    const fieldName = e.target.name;
    if (state.validation_errors[fieldName]) {
      dispatch({
        type: ACTIONS.CLEAR_FIELD_ERROR,
        payload: fieldName
      });
    }

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { [`${fieldName}`]: e.target.value }
    });
  };

  // Enhanced field setter for validated fields
  const handleValidatedFieldChange = (name: string, value: string | number) => {
    // Clear any existing error for this field when user starts typing
    if (state.validation_errors[name]) {
      dispatch({
        type: ACTIONS.CLEAR_FIELD_ERROR,
        payload: name
      });
    }

    dispatch({
      type: ACTIONS.SET_FIELD,
      payload: { [name]: value }
    });
  };

  const handleClearError = (fieldName: string) => {
    dispatch({
      type: ACTIONS.CLEAR_FIELD_ERROR,
      payload: fieldName
    });
  };

  // Add or Remove position_type_ids from state.profile.position_type_ids
  const togglePositionType = (e) => {
    const position_type_id = parseInt(e.target.value)
    const position_type_ids = state.profile.position_type_ids

    if (position_type_ids.includes(position_type_id)) {
      const index = position_type_ids.indexOf(position_type_id)
      position_type_ids.splice(index, 1)
    } else {
      position_type_ids.push(position_type_id)
    }

    setField({ target: { name: 'position_type_ids', value: position_type_ids } })
  }

  const { hiring_types } = state.options

  return (
    <div>
      <div className="form-section-card">
        <h3><i className="fas fa-calendar-check"></i> Position Availability</h3>
        <div style={{ marginBottom: '1.5rem' }}>
          <JobTypes />
        </div>
        <p className="form-text text-muted">
          Select the dental positions you're qualified for
        </p>
        <ProTip variant="green" icon="fas fa-tooth">
          Select all positions you're qualified for to maximize your opportunities. You can always decline offers that don't match your preferences.
        </ProTip>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-handshake"></i> Hiring Availability</h3>
        <div className="modern-check-group">
          {hiring_types.map((h) => (
            <div key={h.id} className="check-item">
              <input
                type="checkbox"
                id={`position_type_${h.id}`}
                name="position_type_ids"
                value={h.id}
                onChange={togglePositionType}
                checked={state.profile.position_type_ids.includes(h.id)}
              />
              <label htmlFor={`position_type_${h.id}`}>{h.name}</label>
            </div>
          ))}
        </div>
        <ProTip variant="green" icon="fas fa-users">
          Being open to different hiring types (temporary, permanent, or both) gives you more flexibility and increases your chances of finding the perfect fit.
        </ProTip>
      </div>

      <div className="form-section-card">
        <h3><i className="fas fa-dollar-sign"></i> Compensation & Travel</h3>

        <div className="row">
          <div className="col-12 col-md-6">
            <ValidatedFormField
              id="hourly_rate"
              label="Desired Hourly Rate ($)"
              type="number"
              value={state.profile.hourly_rate || ""}
              error={state.validation_errors.hourly_rate}
              onChange={handleValidatedFieldChange}
              onClearError={handleClearError}
              validationFn={validateHourlyRate}
              enableRealTimeValidation={true}
              placeholder="25"
              step={1}
              min={1}
              max={999}
            />
            <div className="form-text text-muted mb-3">
              Optional. Whole dollars per hour, $1 to $999.
            </div>
          </div>

          <div className="col-12 col-md-6">
            <ValidatedFormField
              id="maximum_traveling_distance"
              label="Maximum Traveling Distance (Miles)"
              type="number"
              value={state.profile.maximum_traveling_distance || ""}
              error={state.validation_errors.maximum_traveling_distance}
              onChange={handleValidatedFieldChange}
              onClearError={handleClearError}
              validationFn={validateTravelDistance}
              enableRealTimeValidation={true}
              placeholder="50"
            />
            <div className="form-text text-muted mb-3">
              How far are you willing to travel for assignments? (1-500 miles)
            </div>
          </div>
        </div>

        <ProTip variant="green" icon="fas fa-lightbulb">
          Setting competitive rates and reasonable travel distances helps you get more job opportunities. You can always negotiate rates during the hiring process.
        </ProTip>
      </div>
    </div>
  );
}
